import { CommonModule } from "@angular/common";

import { Component, EventEmitter, Input, Output } from "@angular/core";

import { FormsModule } from "@angular/forms";

import { finalize } from "rxjs/operators";

import {
  GlogetSalonSetup,
  GlogetServiceSetup,
  IntegrationsService,
  SquareServiceSetup,
  SquareSetupResponse,
} from "../../../../../../shared/services/integrations.service";

interface CreateGlogetServiceForm {
  square_variation_id: string;

  salon_id: string;

  name: string;

  price: string;

  duration_minutes: number;
}

@Component({
  selector: "app-square-services",

  standalone: true,

  imports: [CommonModule, FormsModule],

  templateUrl: "./square-services.component.html",

  styleUrl: "./square-services.component.scss",
})
export class SquareServicesComponent {
  @Input({
    required: true,
  })
  setup!: SquareSetupResponse;

  @Output()
  mappingChanged = new EventEmitter<void>();

  selectedServiceBySquareVariation: Record<string, string> = {};

  mappingVariationId: string | null = null;

  creatingGlogetVariationId: string | null = null;

  creatingSquareServiceId: string | null = null;

  successMessage = "";

  errorMessage = "";

  /* ==========================================================
     Create in gloget modal
  ========================================================== */

  showCreateGlogetModal = false;

  selectedSquareService: SquareServiceSetup | null = null;

  createGlogetSubmitting = false;

  createGlogetError = "";

  createGlogetForm: CreateGlogetServiceForm = this.emptyCreateForm();

  constructor(private readonly integrationsService: IntegrationsService) {}

  /* ==========================================================
     Data
  ========================================================== */

  get squareServices(): SquareServiceSetup[] {
    return this.setup?.services.square ?? [];
  }

  get glogetServices(): GlogetServiceSetup[] {
    return this.setup?.services.gloget ?? [];
  }

  get glogetSalons(): GlogetSalonSetup[] {
    return this.setup?.locations.gloget ?? [];
  }

  get unmappedGlogetServices(): GlogetServiceSetup[] {
    const mappedIds = new Set(
      this.squareServices
        .filter((service) => !!service.mapping)
        .map((service) => service.mapping!.service_id),
    );

    return this.glogetServices.filter((service) => !mappedIds.has(service.id));
  }

  availableServicesFor(
    squareService: SquareServiceSetup,
  ): GlogetServiceSetup[] {
    const mappedIds = new Set(
      this.squareServices
        .filter(
          (item) =>
            !!item.mapping && item.variation_id !== squareService.variation_id,
        )
        .map((item) => item.mapping!.service_id),
    );

    return this.glogetServices.filter((service) => !mappedIds.has(service.id));
  }

  /* ==========================================================
     Map existing
  ========================================================== */

  mapExisting(squareService: SquareServiceSetup): void {
    const serviceId =
      this.selectedServiceBySquareVariation[squareService.variation_id];

    if (!serviceId) {
      this.errorMessage = "Select a gloget service first.";

      return;
    }

    this.mappingVariationId = squareService.variation_id;

    this.errorMessage = "";

    this.successMessage = "";

    this.integrationsService
      .mapSquareService(
        squareService.variation_id,

        serviceId,
      )
      .pipe(
        finalize(() => {
          this.mappingVariationId = null;
        }),
      )
      .subscribe({
        next: () => {
          this.successMessage = `${squareService.display_name} has been mapped successfully.`;

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to map Square service:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to map this Square service.";
        },
      });
  }

  /* ==========================================================
     Square -> gloget
  ========================================================== */

  openCreateInGloget(service: SquareServiceSetup): void {
    this.selectedSquareService = service;

    this.createGlogetError = "";

    this.createGlogetForm = {
      square_variation_id: service.variation_id,

      salon_id: "",

      name: service.display_name || service.item_name || "",

      price: this.formatSquarePrice(service),

      duration_minutes: service.duration_minutes ?? 30,
    };

    this.showCreateGlogetModal = true;
  }

  closeCreateGlogetModal(): void {
    if (this.createGlogetSubmitting) {
      return;
    }

    this.showCreateGlogetModal = false;

    this.selectedSquareService = null;

    this.createGlogetError = "";

    this.createGlogetForm = this.emptyCreateForm();
  }

  submitCreateInGloget(): void {
    if (this.createGlogetSubmitting) {
      return;
    }

    this.createGlogetError = "";

    const salonId = this.createGlogetForm.salon_id;

    const name = this.createGlogetForm.name.trim();

    const price = this.createGlogetForm.price.trim();

    const duration = Number(this.createGlogetForm.duration_minutes);

    if (!salonId) {
      this.createGlogetError =
        "Select the gloget salon this service belongs to.";

      return;
    }

    if (!name) {
      this.createGlogetError = "Service name is required.";

      return;
    }

    if (!price || Number.isNaN(Number(price)) || Number(price) < 0) {
      this.createGlogetError = "Enter a valid service price.";

      return;
    }

    if (!duration || duration < 1) {
      this.createGlogetError = "Duration must be at least 1 minute.";

      return;
    }

    this.createGlogetSubmitting = true;

    this.integrationsService
      .createGlogetServiceFromSquare({
        square_variation_id: this.createGlogetForm.square_variation_id,

        salon_id: salonId,

        name,

        price,

        duration_minutes: duration,
      })
      .pipe(
        finalize(() => {
          this.createGlogetSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          const displayName = this.selectedSquareService?.display_name || name;

          this.successMessage = `${displayName} was created in gloget and mapped to Square.`;

          this.closeCreateGlogetModal();

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to create gloget service:", error);

          if (error?.error?.existing_service) {
            this.createGlogetError = `${
              error?.error?.detail ?? "A matching service already exists."
            } Existing service: ${error.error.existing_service.name}`;

            return;
          }

          this.createGlogetError =
            error?.error?.detail ?? "Unable to create this service in gloget.";
        },
      });
  }

  /* ==========================================================
     gloget -> Square
  ========================================================== */

  createInSquare(service: GlogetServiceSetup): void {
    const confirmed = window.confirm(
      `Create "${service.name}" as a Square appointment service?`,
    );

    if (!confirmed) {
      return;
    }

    this.creatingSquareServiceId = service.id;

    this.successMessage = "";

    this.errorMessage = "";

    this.integrationsService
      .createSquareServiceFromGloget(service.id)
      .pipe(
        finalize(() => {
          this.creatingSquareServiceId = null;
        }),
      )
      .subscribe({
        next: () => {
          this.successMessage = `${service.name} was created in Square and mapped to gloget.`;

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to create Square service:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to create this service in Square.";
        },
      });
  }

  /* ==========================================================
     Formatting
  ========================================================== */

  formatSquarePrice(service: SquareServiceSetup): string {
    const amount = service.price_money?.amount;

    if (amount === null || amount === undefined) {
      return "0.00";
    }

    /*
     * Square Money amounts are stored
     * in the smallest currency unit.
     */
    return (amount / 100).toFixed(2);
  }

  squarePriceLabel(service: SquareServiceSetup): string {
    const price = this.formatSquarePrice(service);

    const currency = service.price_money?.currency || "";

    return currency ? `${price} ${currency}` : price;
  }

  private emptyCreateForm(): CreateGlogetServiceForm {
    return {
      square_variation_id: "",

      salon_id: "",

      name: "",

      price: "0.00",

      duration_minutes: 30,
    };
  }
}
